import { register } from 'swiper/element/bundle';
import 'swiper/css';
import 'swiper/css/navigation';
import reviewsSwiperCss from '../thirdparty/reviews-swiper.css?raw';

// Register Swiper web components
register();

export class JunipMediaModal extends HTMLElement {
  private shadow: ShadowRoot;
  private rawReviews: string;
  private reviews: Review[];

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });

    this.rawReviews = this.getAttribute('reviews') || '';
    try {
      const jsonString = this.rawReviews
        .replace(/=>nil/g, ':null')
        .replace(/=>/g, ':');
      this.reviews = JSON.parse(jsonString);
    } catch (error) {
      console.error('Failed to parse reviews JSON:', error);
      this.reviews = [];
    }

    this.render();
  }

  private render(): void {
    this.shadow.innerHTML = /*html*/`
      <style>
        ${reviewsSwiperCss}
      </style>
      <div class="reviews-modal">
        <div class="modal-content">
          <button class="close-button">✕</button>
          <swiper-container class="mySwiper" navigation="true">
            ${this.reviews.map((review) => /*html*/`
              <swiper-slide class="review-slide">
                <div class="review-content">
                  <div class="review-header">
                    <div class="review-row">
                      <div class="stars-container">${this.renderStars(review.rating)}</div>
                      <span class="review-date">${this.formatDate(review.created_at)}</span>
                    </div>
                    <h3 class="review-title">${review.title || ''}</h3>
                    <div class="review-row">
                      <p class="review-author">${review.customer_name || ''}</p>
                      ${review.verified_buyer ? this.renderVerifiedBadge() : ''}
                    </div>
                  </div>
                  <div class="review-row">
                    <span class="attributes-container">${this.renderAttributes(review)}</span>
                  </div>
                  <p class="review-body">${review.body || ''}</p>
                  ${review.photo_urls?.length + review.video_urls?.length > 1 ? /*html*/`
                    <div class="review-row">
                      ${this.renderThumbnails(review)}
                    </div>
                    ` : ''}
                </div>
                <div class="main-media">
                  ${this.renderMainMedia(review)}
                </div>
              </swiper-slide>
            `).join('')}
          </swiper-container>
        </div>
      </div>
    `;
    this.addEventListeners();
  }

  private formatDate(dateString: Date): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 Day Ago';
    return `${diffDays} Days Ago`;
  }

  private renderAttributes(review: Review): string {
    if (!review.attribute_records?.length) return '';

    return /*html*/`
      <div class="review-attributes">
        ${review.attribute_records.map((attr: any) => /*html*/`
          <div class="review-attribute">
            <span class="review-label">${attr.name}:</span>
            <span class="review-label-bold">${attr.value}</span>
          </div>
        `).join('')}
      </div>
    `;
  }

  private renderMainMedia(review: Review): string {
    if (review.video_urls?.length > 0) {
      return /*html*/`
        <video class="main-media-image" controls autoplay loop muted src="${review.video_urls?.[0]?.original || ''}"
        alt="${review.title}" />
      `;
    }
    return /*html*/`
      <img class="main-media-image" src="${review.photo_urls?.[0]?.large || ''}" alt="${review.title}" />
    `;
  }

  private renderStars(rating: number): string {
    if (!rating) return '';
    let stars = '';
    for (let i = 0; i < 5; i++) {
      if (i < rating) {
        stars += /*html*/`
          <svg class="stars__item" width="21" height="19.6" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.58589 1.13274C6.93297 0.326779 8.07571 0.326779 8.42279 1.13274L9.56678 3.7892C9.71156 4.12538 10.0284 4.3556 10.3929 4.3894L13.2728 4.65651C14.1466 4.73755 14.4997 5.82436 13.8405 6.40351L11.6675 8.3124C11.3926 8.55397 11.2715 8.92648 11.352 9.28354L11.9879 12.1051C12.1809 12.9611 11.2564 13.6328 10.5018 13.1848L8.0149 11.7081C7.70018 11.5212 7.3085 11.5212 6.99378 11.7081L4.50685 13.1848C3.75232 13.6328 2.82782 12.9611 3.02076 12.1051L3.65668 9.28354C3.73715 8.92648 3.61612 8.55397 3.34113 8.3124L1.1682 6.40351C0.508941 5.82436 0.862067 4.73755 1.73584 4.65651L4.61579 4.3894C4.98025 4.3556 5.29712 4.12538 5.4419 3.7892L6.58589 1.13274Z" fill="#4BBFFF"/>
          </svg>
        `;
      } else {
        stars += /*html*/`
          <svg class="stars__item" width="21" height="19.6" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.58589 1.13274C6.93297 0.326779 8.07571 0.326779 8.42279 1.13274L9.56678 3.7892C9.71156 4.12538 10.0284 4.3556 10.3929 4.3894L13.2728 4.65651C14.1466 4.73755 14.4997 5.82436 13.8405 6.40351L11.6675 8.3124C11.3926 8.55397 11.2715 8.92648 11.352 9.28354L11.9879 12.1051C12.1809 12.9611 11.2564 13.6328 10.5018 13.1848L8.0149 11.7081C7.70018 11.5212 7.3085 11.5212 6.99378 11.7081L4.50685 13.1848C3.75232 13.6328 2.82782 12.9611 3.02076 12.1051L3.65668 9.28354C3.73715 8.92648 3.61612 8.55397 3.34113 8.3124L1.1682 6.40351C0.508941 5.82436 0.862067 4.73755 1.73584 4.65651L4.61579 4.3894C4.98025 4.3556 5.29712 4.12538 5.4419 3.7892L6.58589 1.13274Z" fill="none" stroke="#4BBFFF"/>
          </svg>
        `;
      }
    }
    return stars;
  }

  private renderVerifiedBadge(): string {
    return /*html*/`
      <div class="verified-badge-container">
        <div
          class="verified__badge"
          style="background-color: var(--junipButtonColor);"
        >
          <svg
            height="8"
            viewBox="0 0 10 8"
            width="10"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
          >
            <title>Verified checkmark</title>
            <path fill="var(--junipButtonTextColor)" d="m9.7232.237409c.3475.33708.371.908431.0524 1.276151l-5.36584 6.19354c-.15733.1816-.37795.2873-.61073.2927-.23278.0053-.45753-.0901-.62217-.2643l-2.926832-3.0968c-.3333705-.3528-.3333705-.92464 0-1.27737.33337-.35274.873882-.35274 1.207262 0l2.29638 2.42967 4.76343-5.498101c.3185-.3677198.8585-.3925598 1.2061-.05549z"></path>
          </svg>
        </div>
        <span class="label-small">Verified Buyer</span>
      </div>
    `;
  }

  private addEventListeners(): void {
    const modal = this.shadow.querySelector('.reviews-modal') as HTMLDivElement;
    const closeButton = this.shadow.querySelector('.close-button') as HTMLButtonElement;

    closeButton?.addEventListener('click', () => {
      modal.classList.remove('open');
    });

    modal?.addEventListener('click', (event: MouseEvent) => {
      if (event.target === modal) {
        modal.classList.remove('open');
      }
    });

    const allThumbnails = this.shadow.querySelectorAll('.thumbnail-container');
    allThumbnails?.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        console.log('Thumbnail clicked');
        const slide = thumbnail.closest('.review-slide');
        if (!slide) return;
        const mainMedia = slide?.querySelector('.main-media');
        if (!mainMedia) return;
        const type = thumbnail.getAttribute('data-type');
        const src = thumbnail.getAttribute('data-src');
        if (type === 'video') {
          mainMedia.innerHTML = /*html*/`
            <video class="main-media-video" controls autoplay muted src="${src}" alt="Video" />
          `;
        } else {
          mainMedia.innerHTML = /*html*/`
            <img class="main-media-image" src="${src}" alt="Image" />
          `;
        }
      });
    });
  }

  private renderThumbnails(review: Review): string {
    const thumbnails: string[] = [];
    if (review.photo_urls?.length) {
      review.photo_urls.forEach((photo, index) => {
        thumbnails.push(/*html*/`
          <div class="thumbnail-container" data-type="image" data-index="${index}" data-src="${photo.large}">
            <img class="thumbnail" src="${photo.thumbnail}" alt="Thumbnail ${index + 1}" />
          </div>
        `);
      });
    }
    if (review.video_urls?.length) {
      review.video_urls.forEach((video, index) => {
        thumbnails.push(/*html*/`
          <div class="thumbnail-container" data-type="video" data-index="${index}" data-src="${video.original}">
            <img class="thumbnail" src="${video.thumbnail || ''}" alt="Video thumbnail ${index + 1}" />
          </div>
        `);
      });
    }
    return thumbnails.join('');
  }
}

export interface Review {
  id: number;
  body: string;
  created_at: Date;
  customer_id: number;
  featured: boolean;
  pinned: boolean;
  product_id: number;
  rating: number;
  respondent_display_name: null;
  response: null;
  target_title: string;
  title: string;
  verified_buyer: boolean;
  would_recommend: boolean;
  photo_urls: PhotoURL[];
  video_urls: any[];
  attribute_records: AttributeRecord[];
  customer_name: string;
  remote_id: string;
}

export interface AttributeRecord {
  id: number;
  created_at: Date;
  definition_id: number;
  name: string;
  updated_at: Date;
  value: string;
  value_type: string;
}

export interface PhotoURL {
  large: string;
  thumbnail: string;
}